import { useEffect, useRef, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import ColumnFilterPopover from "./ColumnFilterPopover";
import { moveColumn } from "./tableLayout";

const ARIA_SORT = { asc: "ascending", desc: "descending" };

const HeaderCell = ({ header, table, dragKeyRef }) => {
    const { column } = header;
    const config = column.columnDef.meta?.config ?? {};
    const title = config.header || "";
    const [filterOpen, setFilterOpen] = useState(false);
    const filterButtonRef = useRef(null);
    const sorted = column.getIsSorted();
    const filtered = column.getIsFiltered();
    const draggable = config.persist !== false;
    const { isResizingColumn, deltaOffset } = table.getState().columnSizingInfo;

    // Armado ao pressionar o puxador de redimensionar; desarmado no próximo mouseup/touchend.
    // Evita depender do timing de render para saber se o gesto é resize ou drag.
    const resizeArmedRef = useRef(false);
    const disarmRef = useRef(null);
    useEffect(() => () => disarmRef.current?.(), []);
    const armResize = () => {
        const disarm = () => {
            resizeArmedRef.current = false;
            document.removeEventListener("mouseup", disarm);
            document.removeEventListener("touchend", disarm);
            disarmRef.current = null;
        };
        disarmRef.current?.();
        resizeArmedRef.current = true;
        disarmRef.current = disarm;
        document.addEventListener("mouseup", disarm, { once: true });
        document.addEventListener("touchend", disarm, { once: true });
    };
    const resizeHandler = header.getResizeHandler();
    const onResizeStart = (event) => {
        armResize();
        resizeHandler(event);
    };

    const onDragStart = (event) => {
        if (resizeArmedRef.current || isResizingColumn) {
            event.preventDefault();
            return;
        }
        dragKeyRef.current = column.id;
        event.dataTransfer?.setData?.("text/plain", column.id);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    };
    const onDragEnd = () => {
        dragKeyRef.current = null;
    };
    const onDragOver = (event) => {
        if (draggable && dragKeyRef.current && dragKeyRef.current !== column.id) event.preventDefault();
    };
    const onDrop = (event) => {
        event.preventDefault();
        const from = dragKeyRef.current;
        dragKeyRef.current = null;
        if (draggable && from && from !== column.id) {
            table.setColumnOrder((order) => moveColumn(order, from, column.id));
        }
    };

    return (
        <th
            colSpan={header.colSpan}
            style={{ width: header.getSize() }}
            aria-sort={ARIA_SORT[sorted] ?? "none"}
            draggable={draggable}
            onDragStart={draggable ? onDragStart : undefined}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className={`relative select-none border-b border-primary/40 px-2 py-2 text-center text-xs font-semibold uppercase text-laranjinha/80 ${draggable ? "cursor-grab" : ""}`}
        >
            <div className="flex items-center justify-center gap-1">
                {column.getCanSort() ? (
                    <button type="button" onClick={column.getToggleSortingHandler()} className="truncate hover:text-laranjinha">
                        {flexRender(column.columnDef.header, header.getContext())}
                        {sorted === "asc" ? " ▲" : sorted === "desc" ? " ▼" : ""}
                    </button>
                ) : (
                    <span className="truncate">{flexRender(column.columnDef.header, header.getContext())}</span>
                )}
                {column.getCanFilter() && config.filter !== "ids" ? (
                    <button
                        ref={filterButtonRef}
                        type="button"
                        aria-label={`Filtrar ${title}`}
                        title={filtered ? `Filtro ativo em ${title}` : `Filtrar ${title}`}
                        aria-haspopup="dialog"
                        aria-expanded={filterOpen}
                        onClick={() => setFilterOpen((open) => !open)}
                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded ${
                            filtered ? "bg-primary text-black" : "text-laranjinha hover:bg-mgs-surface hover:text-primary"
                        }`}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill={filtered ? "currentColor" : "none"}
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M3 4h18l-7 8v6l-4 2v-8z" />
                        </svg>
                    </button>
                ) : null}
            </div>
            {column.getCanResize() ? (
                <div
                    role="separator"
                    aria-label={`Redimensionar ${title}`}
                    onMouseDown={onResizeStart}
                    onTouchStart={onResizeStart}
                    onDoubleClick={() => column.resetSize()}
                    className={`absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none ${column.getIsResizing() ? "bg-primary" : "hover:bg-primary/60"}`}
                    style={column.getIsResizing() ? { transform: `translateX(${deltaOffset ?? 0}px)` } : undefined}
                />
            ) : null}
            {filterOpen ? (
                <ColumnFilterPopover column={column} anchorRef={filterButtonRef} onClose={() => setFilterOpen(false)} />
            ) : null}
        </th>
    );
};

export default HeaderCell;
